import { useRef, useState } from "react";

import _ from "lodash";

import Sentry from "@similarweb/sentry-logger";

import categoryService, { ECategoryNameValidation } from "app/common/services/categoryService";
import { ECategoryType } from "app/common/services/categoryService.types";
import { isLocked } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { MAX_DOMAINS_IN_CATEGORY } from "app/components/customCategoriesWizard/custom-categories-max-domains";
import { useExceededLimitation } from "app/components/metering/UseLimitation";
import { SWReactTableWrapperContextConsumer } from "app/components/React/Table/SWReactTableWrapperSelectionContext";
import { translate } from "app/filters/i18nFilter";
import { UserCustomCategoryService } from "app/services/category/userCustomCategoryService";
import { allTrackers } from "app/services/track/track";
import { TableSelection } from "pro-features/components/TableSelection/src/TableSelection";
import { TableSelectionGroupWebsiteComponent } from "pro-features/components/TableSelection/src/TableSelectionGroupWebsiteComponent";
import { ETableSelectionNewGroupDropdownMode } from "pro-features/components/TableSelection/src/TableSelectionNewGroupDropdown";
import { isIndustryAnalysisLimitationExceeded } from "pro-features/pages/module start page/src/industry analysis/utils/metering/IndustryAnalysisMetering";
import { proNavigator } from "single-spa/react-app/ProNavigator";

type Item = {
    id: string;
    text: string;
};

export const DomainSelection = (props: FixmeType) => {
    const {
        selectedRows,
        clearAllSelectedRows,
        showToast,
        appendTo = ".swReactTable-header-wrapper",
        initialIsVisible = false,
        createGroupButtonKey,
        addToGroupButtonKey,
        searchListsPlaceholderKey,
        targetStateForGroupVisit = "market_research_overview_v2",
        keywordGapAnalysis,
        keywordGapAnalysisTargetState,
        entity,
    } = props;
    const dropdownRef = useRef(null);

    const [newGroupLoading, setNewGroupLoading] = useState(false);
    const [isCreateNewListModalOpen, setIsCreateNewListModalOpen] = useState(false);
    const [newGroupType, setNewGroupType] = useState(ECategoryType.GENERAL_LIST);
    const [newGroupError, setNewGroupError] = useState(false);
    const [newGroupErrorMessage, setNewGroupErrorMessage] = useState<string | undefined>(undefined);
    const [tableSelectionMode, setTableSelectionMode] = useState(ETableSelectionNewGroupDropdownMode.GROUP_LIST);
    const [currentGroup, setCurrentGroup] = useState<string | undefined>(undefined);
    const [linkToGroup, setLinkToGroup] = useState<string | undefined>(undefined);

    const isLimitationExceeded = useExceededLimitation(isIndustryAnalysisLimitationExceeded);
    const isCreatingCustomIndustryHooked =
        isLimitationExceeded || isLocked(swSettings.components.IndustryAnalysisMarketBuilder);

    const showGroupLinkToast = (name: string, text: string, label: string, isNewGroup: boolean) => {
        const params = proNavigator.getParams();
        const categoryObject = UserCustomCategoryService.getCustomCategoryByName(name);

        const targetState = targetStateForGroupVisit;
        const updatedLinkToGroup = proNavigator.href(targetState, {
            ...params,
            category: categoryObject?.forUrl,
        });

        if (isNewGroup) {
            setLinkToGroup(updatedLinkToGroup);
        } else {
            showToast(updatedLinkToGroup, text, label);
        }
    };

    const showSuccessToast = (name: string, isNewGroup: boolean) => {
        clearAllSelectedRows();
        setTableSelectionMode(ETableSelectionNewGroupDropdownMode.GROUP_LIST);
        setNewGroupType(ECategoryType.GENERAL_LIST);
        setNewGroupErrorMessage(undefined);
        setNewGroupError(false);
        if (!isNewGroup) {
            setIsCreateNewListModalOpen(false);
        }
        const text = isNewGroup
            ? translate(
                  selectedRows.length === 1
                      ? "table.selection.website.custom.industry.groupcreated"
                      : "table.selection.websites.custom.industry.groupcreated",
                  {
                      groupName: name,
                      number_of_sites: selectedRows.length.toString(),
                  },
              )
            : translate(
                  selectedRows.length === 1
                      ? "table.selection.website.custom.industry.groupupdated"
                      : "table.selection.websites.custom.industry.groupupdated",
                  {
                      groupName: name,
                      number_of_sites: selectedRows.length.toString(),
                  },
              );
        showGroupLinkToast(name, text, translate("table.selection.websites.analyze.industry"), isNewGroup);
    };

    const tableSelectionOnCancel = () => {
        setTableSelectionMode(ETableSelectionNewGroupDropdownMode.GROUP_LIST);
        setNewGroupError(false);
        setNewGroupErrorMessage(undefined);
        setIsCreateNewListModalOpen(false);
        setNewGroupType(ECategoryType.GENERAL_LIST);
        setLinkToGroup(undefined);
    };
    const getSelectedRowsFromStore = () => {
        return selectedRows.map(({ Domain, domain }: FixmeType) => Domain || domain);
    };
    const tableSelectionOnSubmit = async (name: string) => {
        if (!name) {
            setNewGroupErrorMessage(translate("customcategories.wizard.error.emptyname"));
            setNewGroupError(true);
            return;
        }
        const nameIsValid = categoryService.validateGroupName(name);
        if (nameIsValid === ECategoryNameValidation.ILLEGAL_EXIST) {
            setNewGroupErrorMessage(translate("customcategories.wizard.error.duplicatename"));
            setNewGroupError(true);
            return;
        }
        if (nameIsValid === ECategoryNameValidation.ILLEGAL_LENGTH) {
            setNewGroupErrorMessage(translate("customcategories.wizard.error.illegallength"));
            setNewGroupError(true);
            return;
        }
        if (nameIsValid === ECategoryNameValidation.ILLEGAL_CHARS) {
            setNewGroupErrorMessage(translate("customcategories.wizard.error.illegalchars"));
            setNewGroupError(true);
            return;
        }

        const selectedDomains = getSelectedRowsFromStore();

        allTrackers.dispatchTrackEvent(
            "Button",
            "click",
            `Create New Custom Industry/Save/${name || ""}/${selectedDomains.length || ""}`,
        );
        setNewGroupLoading(true);
        try {
            await UserCustomCategoryService.addCustomCategory({
                name,
                domains: selectedDomains,
                categoryType: newGroupType,
            });

            showSuccessToast(name, true);
        } catch (e) {
            Sentry.error("error adding custom category", e);
        } finally {
            setNewGroupLoading(false);
        }
    };
    const getGroups = () => {
        return UserCustomCategoryService.getCustomCategories().map(({ text, categoryId: id }) => ({
            text,
            id,
        }));
    };

    const onNewGroupClick = () => {
        if (selectedRows.length > MAX_DOMAINS_IN_CATEGORY) {
            setTableSelectionMode(ETableSelectionNewGroupDropdownMode.MAX_ITEMS);
        } else {
            setTableSelectionMode(ETableSelectionNewGroupDropdownMode.NEW_GROUP);
        }
    };

    const onExistingGroupClick = async ({ id, text }: Item) => {
        const currentGroupFromService = UserCustomCategoryService.getCustomCategoryById(id);
        // @ts-ignore FIXME: strict migration - getCustomCategoryById returns ICategory | undefined and ICategory.domains is optional — click flow guarantees an existing group with domains for the given id
        const currentDomains: string[] = currentGroupFromService.domains;
        const selectedDomains = getSelectedRowsFromStore();
        const withoutDuplications = _.uniq([...currentDomains, ...selectedDomains]);
        const newDomainsCount = withoutDuplications.length - currentDomains.length;

        // if no new domains were added
        if (newDomainsCount === 0) {
            setTableSelectionMode(ETableSelectionNewGroupDropdownMode.ALL_ITEMS_EXISTS);
            setCurrentGroup(text);
        }

        // new domains added
        else {
            // check number of domains in category
            if (withoutDuplications.length > MAX_DOMAINS_IN_CATEGORY) {
                setTableSelectionMode(ETableSelectionNewGroupDropdownMode.MAX_ITEMS);
            } else {
                allTrackers.dispatchTrackEvent(
                    "Button",
                    "click",
                    `Add to Custom Industry/Save/${text || ""}/${newDomainsCount.toString() || ""}`,
                );
                try {
                    const update = await UserCustomCategoryService.updateCustomCategory({
                        domains: withoutDuplications,
                        id,
                        name: text,
                    });

                    if (update) {
                        showSuccessToast(text, false);
                    }
                } catch (e) {}
            }
        }
    };

    const onGroupClick = async (item: Item | Omit<Item, "id">) => {
        // click on new group
        if (!("id" in item && item.id)) {
            onNewGroupClick();
            allTrackers.trackEvent("add to Custom Category", "click", "create new group");
        }
        // click on existing group
        else {
            await onExistingGroupClick(item);
        }
    };

    const onNewListButtonClick = () => {
        setIsCreateNewListModalOpen(true);
    };
    const getDropdownComponent = () => {
        return (
            <TableSelectionGroupWebsiteComponent
                appendTo={appendTo}
                onNewListButtonClick={onNewListButtonClick}
                onCancel={tableSelectionOnCancel}
                isCreateNewListModalOpen={isCreateNewListModalOpen}
                newGroupNameLabel={translate("table.selection.newgroup.websites.title")}
                groups={getGroups()}
                groupIconName="category"
                onGroupClick={onGroupClick}
                isLoading={newGroupLoading}
                onSubmit={tableSelectionOnSubmit}
                errorMessage={newGroupErrorMessage}
                error={newGroupError}
                selectedListType={newGroupType}
                ref={dropdownRef}
                mode={tableSelectionMode}
                newGroupItemText="table.selection.newgroup.websites.create"
                currentGroup={currentGroup}
                allItemsExistsMessage="table.selection.websites.exists"
                maxItemsMessage="table.selection.websites.max.websites"
                count={selectedRows?.length || 0}
                linkToGroup={linkToGroup}
                createGroupButtonKey={createGroupButtonKey}
                addToGroupButtonKey={addToGroupButtonKey}
                searchListsPlaceholderKey={searchListsPlaceholderKey}
                isCreateNewListHooked={isCreatingCustomIndustryHooked}
                selectedRows={selectedRows}
                keywordGapAnalysis={keywordGapAnalysis}
                keywordGapAnalysisTargetState={keywordGapAnalysisTargetState}
                entity={entity}
            />
        );
    };
    return (
        <SWReactTableWrapperContextConsumer>
            {({ selectedRows, clearAllSelectedRows }) => {
                const text = translate("incomingtraffic.table.selection.selected", {
                    count: selectedRows.length.toString(),
                    entity,
                });
                const onCloseClick = () => {
                    clearAllSelectedRows();
                };
                return (
                    <TableSelection
                        showSeparator={false}
                        key="1"
                        selectedText={text}
                        onCloseClick={onCloseClick}
                        addToGroupLabel=""
                        isVisible={selectedRows.length > 0 || initialIsVisible}
                        groupSelectorElement={getDropdownComponent()}
                        isDisabled={selectedRows?.length === 0}
                    />
                );
            }}
        </SWReactTableWrapperContextConsumer>
    );
};
